import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {
    Collapse,
    FormControlLabel,
    FormGroup,
    RadioGroup
} from '@mui/material';
import { FunctionComponent } from 'react';

import { PlanningQuestion } from '../../../../types/work';
import {
    ActionButtonsRow,
    FreeTextField,
    OptionCheckbox,
    OptionLabel,
    OptionRadio,
    QuestionBody,
    QuestionCard,
    QuestionExpandIcon,
    QuestionHeader,
    QuestionText,
    QuestionTitleRow,
    SkipButton,
    SkipButtonRow,
    SkippedLabel,
    SubmitAnswersButton
} from './styled';
import { Answer, FREE_TEXT_OPTION } from './types';

interface Props {
    questions: PlanningQuestion[];
    answers: Record<number, Answer>;
    expandedQuestions: Set<number>;
    skipped: Set<number>;
    onToggleQuestion: (idx: number) => void;
    onSkip: (idx: number) => void;
    onSingleChange: (qIdx: number, value: string) => void;
    onMultiToggle: (qIdx: number, value: string) => void;
    onFreeTextChange: (qIdx: number, value: string) => void;
    onSubmit: () => void;
    isSubmitting?: boolean;
}

const QuestionsList: FunctionComponent<Props> = ({
    questions,
    answers,
    expandedQuestions,
    skipped,
    onToggleQuestion,
    onSkip,
    onSingleChange,
    onMultiToggle,
    onFreeTextChange,
    onSubmit,
    isSubmitting = false
}) => (
    <>
        {questions.map((q, idx) => {
            const isOpen = expandedQuestions.has(idx);
            const isSkipped = skipped.has(idx);
            const showFreeText =
                answers[idx]?.selected.has(FREE_TEXT_OPTION) ?? false;
            const freeTextValue = answers[idx]?.freeText ?? '';
            const singleValue =
                Array.from(answers[idx]?.selected ?? [])[0] ?? '';

            return (
                <QuestionCard key={idx} elevation={0}>
                    <QuestionHeader onClick={() => onToggleQuestion(idx)}>
                        <QuestionTitleRow>
                            <QuestionText>{`${idx + 1}. ${q.question}`}</QuestionText>
                            {isSkipped && (
                                <SkippedLabel>(Skipped)</SkippedLabel>
                            )}
                        </QuestionTitleRow>
                        <QuestionExpandIcon size="small">
                            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </QuestionExpandIcon>
                    </QuestionHeader>

                    <Collapse in={isOpen}>
                        <QuestionBody>
                            {q.type === 'multiSelect' ? (
                                <FormGroup>
                                    {q.options.map((opt) => (
                                        <FormControlLabel
                                            key={opt.value}
                                            control={
                                                <OptionCheckbox
                                                    checked={
                                                        answers[
                                                            idx
                                                        ]?.selected.has(
                                                            opt.value
                                                        ) ?? false
                                                    }
                                                    onChange={() =>
                                                        onMultiToggle(
                                                            idx,
                                                            opt.value
                                                        )
                                                    }
                                                />
                                            }
                                            label={
                                                <OptionLabel>
                                                    {opt.label}
                                                </OptionLabel>
                                            }
                                        />
                                    ))}
                                    <FormControlLabel
                                        control={
                                            <OptionCheckbox
                                                checked={
                                                    answers[idx]?.selected.has(
                                                        FREE_TEXT_OPTION
                                                    ) ?? false
                                                }
                                                onChange={() =>
                                                    onMultiToggle(
                                                        idx,
                                                        FREE_TEXT_OPTION
                                                    )
                                                }
                                            />
                                        }
                                        label={
                                            <OptionLabel>
                                                Other (specify below)
                                            </OptionLabel>
                                        }
                                    />
                                </FormGroup>
                            ) : (
                                <RadioGroup
                                    value={singleValue}
                                    onChange={(e) =>
                                        onSingleChange(idx, e.target.value)
                                    }
                                >
                                    {q.options.map((opt) => (
                                        <FormControlLabel
                                            key={opt.value}
                                            value={opt.value}
                                            control={<OptionRadio />}
                                            label={
                                                <OptionLabel>
                                                    {opt.label}
                                                </OptionLabel>
                                            }
                                        />
                                    ))}
                                    <FormControlLabel
                                        value={FREE_TEXT_OPTION}
                                        control={<OptionRadio />}
                                        label={
                                            <OptionLabel>
                                                Other (specify below)
                                            </OptionLabel>
                                        }
                                    />
                                </RadioGroup>
                            )}

                            {showFreeText && (
                                <FreeTextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    placeholder="Type your answer..."
                                    value={freeTextValue}
                                    onChange={(e) =>
                                        onFreeTextChange(idx, e.target.value)
                                    }
                                    variant="outlined"
                                />
                            )}

                            <SkipButtonRow>
                                <SkipButton
                                    variant="outlined"
                                    size="small"
                                    onClick={() => onSkip(idx)}
                                >
                                    Skip
                                </SkipButton>
                            </SkipButtonRow>
                        </QuestionBody>
                    </Collapse>
                </QuestionCard>
            );
        })}
        <ActionButtonsRow>
            <SubmitAnswersButton
                variant="contained"
                onClick={onSubmit}
                disabled={isSubmitting}
            >
                Submit Answers
            </SubmitAnswersButton>
        </ActionButtonsRow>
    </>
);

export default QuestionsList;
